import classNames from "classnames";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import "./create-workflow-template-card.scss";
import { useTemplatePreviewSrc } from "./create-workflow-template-preview";
import { getTemplateTagColor, IProcessTemplate } from "./create-workflow-templates";

export interface ICreateWorkflowTemplateCardProps {
  template: IProcessTemplate;
  onUse: (template: IProcessTemplate) => void;
  onViewDetails: (template: IProcessTemplate) => void;
  /** This card's template is the one whose process is being created right now. */
  isCreating?: boolean;
  /** Another template's process is being created — only one creation runs at a time. */
  disabled?: boolean;
}

/**
 * One process-template tile: a read-only BPMN thumbnail cover carrying the tag chip,
 * over a body with the template name and its meta line. On hover, or when focus enters
 * the card, the ui-kit template cards' action bar rises over the bottom of the cover —
 * reachable by keyboard rather than mouse-only — and it stays up while this template's
 * process is being created, so the pending "Use template" never vanishes mid-request.
 */
const CreateWorkflowTemplateCard = ({
  template,
  onUse,
  onViewDetails,
  isCreating = false,
  disabled = false
}: ICreateWorkflowTemplateCardProps) => {
  // Each card renders its own template's diagram, cached per template id.
  const previewSrc: string | undefined = useTemplatePreviewSrc(template.id, template.xml);

  return (
    <div
      className={classNames("kuika-process-template-card", { "kuika-process-template-card--busy": isCreating })}
      aria-busy={isCreating || undefined}
      data-testid={`processTemplateCard-${template.id}`}
    >
      <div className="kuika-process-template-card__cover">
        {/* Decorative artwork (the name/meta below carry the semantics), hence the empty alt. */}
        {previewSrc && <img className="kuika-process-template-card__preview" src={previewSrc} alt="" />}

        <KuikaBadge
          className="kuika-process-template-card__tag"
          color={getTemplateTagColor(template.tag)}
          content={template.tag}
        />

        <div className="kuika-process-template-card__actions">
          {/* `status`, not `loading`: a loading button goes natively disabled, which would
              drop focus out of the card and hide the bar a keyboard user just pressed.
              `md`, not the ui-kit cards' `sm`: this cover is over twice their 112px. */}
          <KuikaButton
            type="primary"
            size="md"
            status={isCreating ? "loading" : "idle"}
            statusLabels={{ loading: "Creating" }}
            disabled={disabled}
            onClick={() => onUse(template)}
            data-testid={`processTemplateUseTemplate-${template.id}`}
          >
            Use template
          </KuikaButton>
          <KuikaButton
            type="secondary"
            size="md"
            disabled={disabled || isCreating}
            onClick={() => onViewDetails(template)}
            data-testid={`processTemplateViewDetails-${template.id}`}
          >
            Details
          </KuikaButton>
        </div>
      </div>

      <div className="kuika-process-template-card__body">
        <KuikaTextMedium className="kuika-process-template-card__title">{template.name}</KuikaTextMedium>
        <span className="kuika-process-template-card__meta">
          <KuikaSmallText>Process Template</KuikaSmallText>
          <KuikaSvg name="layout-columns" width={14} height={14} />
          <KuikaSmallText>{`${template.stepCount} steps`}</KuikaSmallText>
        </span>
      </div>
    </div>
  );
};

export default CreateWorkflowTemplateCard;
